import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserTable from '../components/UserTable';
import AddUsers from '../components/AddUsers';
import SubscriptionChange from '../components/SubscriptionChange';
import OrgSettings from '../components/OrgSettings';
import { Modal, NumberInput, Button, Text } from '@mantine/core';
import { subscriptionApi } from '../apis/subscription.api';
import { stripeApi } from '../apis/stripe.api';
import { addUserToCache, fetchUserCacheApi } from '../apis/admin.api';
import { renewalKind } from '../processes/subscriptionGate';

const organizationNameOf = (organization) => {
  if (!organization) return '';
  if (typeof organization === 'string') return organization;
  return organization.name || '';
};

const Subscription = () => {
  // Modal states
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [showOrgSettingsModal, setShowOrgSettingsModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewSeats, setRenewSeats] = useState(1);
  const [renewLoading, setRenewLoading] = useState(false);
  
  // User data states
  const [orgUsers, setOrgUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  
  // UI states
  const { user } = useSelector((state) => state.user);
  const { subscriptionInfo } = useSelector((state) => state.app);
  const organizationName = organizationNameOf(user.organization);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load organization users
  const loadOrgUsers = useCallback(async () => {
    console.log('Loading organization users...');
    try {
      const response = await subscriptionApi.getOrganizationUsers(user.idToken, organizationName);
      console.log('Org users API response:', response);

      if (!response) {
        throw new Error('No organization user data received');
      }

      // Format active users
      const formattedOrgUsers = response.map(u => ({
        ...u,
        ...u.registryId,
        userName: u.userName || u.name || u.registryId?.name || u.email?.split('@')[0],
        status: u.subscriptionStatus || 'inactive',
        registryId: undefined
      }));

      console.log('Formatted org users:', formattedOrgUsers);
      setOrgUsers(formattedOrgUsers);
      return formattedOrgUsers;
    } catch (error) {
      console.error('Error loading org users:', error);
      toast.error('Failed to load organization users');
      return [];
    }
  }, [user.idToken, organizationName]);

  // Load pending users
  const loadPendingUsers = useCallback(async () => {
    console.log('Loading pending users...');
    try {
      const response = await fetchUserCacheApi(user.idToken);
      console.log('Pending users API response:', response);

      if (!response?.data) {
        console.warn('No pending users data received');
        return [];
      }

      const filteredPendingUsers = response.data
        .filter(pu => organizationNameOf(pu.organization) === organizationName)
        .map(pu => ({
          ...pu,
          userName: pu.name || pu.email.split('@')[0],
          subscriptionStatus: 'pending',
          role: pu.role || 'Trainee'
        }));

      console.log('Filtered pending users:', filteredPendingUsers);
      setPendingUsers(filteredPendingUsers);
      return filteredPendingUsers;
    } catch (error) {
      console.error('Error loading pending users:', error);
      toast.error('Failed to load pending users');
      return [];
    }
  }, [user.idToken, organizationName]);

  // Combine and update all users
  const updateAllUsers = useCallback(async () => {
    console.log('Updating all users...');
    setLoading(true);
    
    try {
        // Get subscription details first
        const subDetails = await subscriptionApi.getSubscriptionDetails(
            user.idToken,
            organizationName
        );
        if (!subDetails?.subscription) {
          throw new Error(subDetails?.error || 'Failed to load subscription details');
        }
        setSubscriptionDetails(subDetails);
        setRenewSeats(subDetails.subscription?.features?.maxUsers || 1);

        const [currentOrgUsers, currentPendingUsers] = await Promise.all([
            loadOrgUsers(),
            loadPendingUsers()
        ]);

        // Create a map of users by email to handle duplicates
        const userMap = new Map();
        
        // Add org users first (they take precedence)
        currentOrgUsers.forEach(user => {
            console.log('Adding org user to map:', user.email);
            userMap.set(user.email, user);
        });

        // Only add pending users if they don't exist in org users
        currentPendingUsers.forEach(pendingUser => {
            if (!userMap.has(pendingUser.email)) {
                console.log('Adding pending user to map:', pendingUser.email);
                userMap.set(pendingUser.email, pendingUser);
            } else {
                console.log('Skipping duplicate pending user:', pendingUser.email);
            }
        });

        const allUsers = Array.from(userMap.values());
        console.log('Final deduped users:', allUsers);
        setCombinedUsers(allUsers);
    } catch (error) {
        console.error('Error updating all users:', error);
        setError('Failed to update users');
    } finally {
        setLoading(false);
    }
  }, [loadOrgUsers, loadPendingUsers, user.idToken, organizationName]);

  // Initial load
  useEffect(() => {
    if (user?.idToken && organizationName) {
      console.log('Initial user data load');
      updateAllUsers();
    }
  }, [user?.idToken, organizationName, updateAllUsers]);

  // Handle status changes
  const handleStatusChange = async (email, newStatus) => {
    console.log('Handling status change:', { email, newStatus });
    setLoading(true);
    
    try {
        // First get subscription details to validate the change
        console.log('Fetching subscription details for:', organizationName);
        const subDetails = await subscriptionApi.getSubscriptionDetails(
            user.idToken, 
            organizationName
        );
        console.log('Subscription details:', subDetails);

        if (!subDetails?.subscription) {
            toast.error('Failed to load subscription details');
            return;
        }

        // Check if user is org leader using subscription details
        if (subDetails.subscription.orgLeader.email === email && newStatus !== 'active') {
            toast.error(
                'Cannot deactivate organization leader. Please change the organization leader in Org Settings first.',
                { autoClose: 6000 }
            );
            return;
        }

        const { planType } = subDetails.subscription.details;
        const { maxUsers } = subDetails.subscription.features;
        const activeUsers = subDetails.users.active.count;

        console.log('Evaluating subscription status:', {
            planType,
            maxUsers,
            activeUsers,
            email,
            newStatus,
            isOrgLeader: subDetails.subscription.orgLeader === email
        });

        // Only allow activation if within plan limits
        if (newStatus === 'active') {
            if (planType !== 'enterprise' && planType !== 'explorer') {
                toast.error('Your plan type does not allow activating users');
                return;
            }
            
            if (activeUsers >= maxUsers) {
                toast.error(`Cannot activate more users. Plan limit is ${maxUsers} users. Select 'Change Subscription' to modify your available seats.`);
                return;
            }
        }

        // Proceed with status update if validation passes
        await subscriptionApi.updateUserSubscriptionStatus(
            user.idToken,
            organizationName,
            email,
            newStatus
        );
        
        console.log('Status updated, refreshing all users');
        await updateAllUsers();
        toast.success(`User status updated to ${newStatus}`);

    } catch (error) {
        console.error('Error updating status:', error);
        toast.error(error.status === 409 ? error.message : 'Failed to update user status');
    } finally {
        setLoading(false);
    }
  };

  // Handle adding new users
  const handleAddUsers = async (users) => {
    console.log('Adding new users:', users);
    setLoading(true);
    
    try {
      for (const userData of users) {
        if (!userData.email) {
          throw new Error('Email is required for new users');
        }

        const newUser = {
          name: (userData.name || '').trim(),
          organization: organizationName,
          email: userData.email.toLowerCase(),
          role: userData.role
        };
        if (!newUser.name) {
          throw new Error('Name is required for new users');
        }

        console.log('Adding user to cache:', newUser);
        const response = await addUserToCache(user.idToken, newUser);
        
        if (response.data.error) {
          throw new Error(`Failed to add user: ${response.data.error}`);
        }
        
        toast.success(`Added ${userData.email} to queue`);
      }

      console.log('Users added, refreshing all users');
      await updateAllUsers();
      setShowAddUsersModal(false);
    } catch (error) {
      console.error('Error adding users:', error);
      toast.error(error.message || 'Failed to add users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUsersClick = () => {
    const totalUsers = combinedUsers.length;
    const maxAllowedUsers = subscriptionDetails?.subscription?.features?.maxUsers || 0;

    if (totalUsers >= maxAllowedUsers) {
      toast.error(
        `Cannot add more users. Your plan limit is ${maxAllowedUsers} seats. Please update your subscription for additional users.`,
        { autoClose: 6000 }
      );
      return;
    }

    setShowAddUsersModal(true);
  };

  const pricePerSeat = Number(process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_4QTR) || 2400;
  const maxExplorerSeats = Number(process.env.REACT_APP_EXPLORER_MAX_SEATS) || 9;
  const expirationDate = subscriptionDetails?.subscription?.details?.endDate;
  const formattedExpiration = expirationDate
    ? new Date(expirationDate).toLocaleDateString()
    : 'Unknown';

  // Renewing fewer seats than are active is allowed; the newest seats beyond
  // what is paid for are switched off afterwards, so say so before paying.
  const activeSeats = subscriptionDetails?.users?.active?.count || 0;
  const seatsSwitchedOff = Math.max(0, activeSeats - (renewSeats || 0));

  const handleRenewConfirm = async () => {
    if (!renewSeats || renewSeats < 1) {
      toast.error('Please enter the number of seats to renew');
      return;
    }
    if (renewSeats > maxExplorerSeats) {
      toast.error(`Explorer plan is limited to ${maxExplorerSeats} seats.`);
      return;
    }

    setRenewLoading(true);
    try {
      const { url } = await stripeApi.createRenewSession({
        seats: renewSeats,
        email: user.email,
        userName: user.name,
        companyName: organizationName,
        region: process.env.REACT_APP_REGION_CODE?.toLowerCase(),
        planType: 'explorer',
        paymentSchedule: 'yearly',
        subscriptionId: subscriptionDetails?.subscription?.details?.id
      }, 'explorer');

      if (!url) {
        throw new Error('No checkout URL received from server');
      }
      window.location.href = url;
    } catch (error) {
      console.error('Renewal checkout failed:', error);
      toast.error(error.message || 'Failed to start renewal checkout');
      setRenewLoading(false);
    }
  };

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <div className="header-row">
          <div className="flex items-center">
            <h1>[{organizationName || 'Organization'}] Subscription</h1>
            {subscriptionDetails?.subscription?.features && (
              <span className="ml-4 text-gray-600">
                (Max Users: {subscriptionDetails.subscription.features.maxUsers})
              </span>
            )}
          </div>
          <div className="flex items-center mt-3 gap-4">
            <button 
              className="btn-add-users"
              onClick={handleAddUsersClick}
            >
              + Add Users
            </button>
            {/* Renewing early through Stripe is an Explorer thing only: an
                Enterprise renewal is a contract, and a trial is not renewed
                (it becomes a customer as a new organisation). */}
            {renewalKind({
              orgType: subscriptionInfo?.orgType,
              planType: subscriptionDetails?.subscription?.details?.planType,
              orgName: organizationName,
            }) === 'explorer' && (
            <button
              className="btn-add-users"
              onClick={() => setShowRenewModal(true)}
              disabled={!subscriptionDetails?.subscription}
            >
              Renew Account
            </button>
            )}
            {subscriptionDetails?.subscription && (
              <span className="text-gray-600">
                Expires {formattedExpiration}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="absolute top-0 right-0 mt-[132px] mr-4 flex space-x-4">
        <button 
          className="btn-help"
          onClick={() => window.open('https://lavenirai.com/support/', '_blank')}
        >
          Help
        </button>
        <button 
          className="btn-org-settings"
          onClick={() => setShowOrgSettingsModal(true)}
        >
          <span className="icon-settings"></span>Org Settings
        </button>
        <button 
          className="btn-change-subscription"
          onClick={() => setShowSubscriptionModal(true)}
        >
          <span className="icon-subscription"></span>Change Subscription
        </button>
      </div>

      {error ? (
        <div className="p-4 text-red-600 bg-red-100 rounded-md m-4">
          <p>{error}</p>
        </div>
      ) : (
        <>
          <UserTable 
            users={combinedUsers}
            loading={loading}
            onStatusChange={handleStatusChange}
            currentOrgLeader={subscriptionDetails?.subscription?.orgLeader}
          />
          <div className="mt-4 p-4 text-sm text-gray-600">
            Users must register at their assigned regional server:<br />
            US = app.lavenirai.com&nbsp;&nbsp;&nbsp;
            EU = app-eu.lavenirai.com&nbsp;&nbsp;&nbsp;
            APAC = app-sg.lavenirai.com
          </div>
        </>
      )}
      
      {showSubscriptionModal && (
        <SubscriptionChange 
          authToken={user.idToken}
          onClose={async () => {
            setShowSubscriptionModal(false);
            await updateAllUsers();
          }}
          organizationId={organizationName}
        />
      )}

      {showAddUsersModal && (
        <AddUsers
          authToken={user.idToken}
          onClose={() => {
            if (!loading) {
              setShowAddUsersModal(false);
            }
          }}
          organizationId={organizationName}
          onSubmit={handleAddUsers}
          loading={loading}
          maxUsers={subscriptionDetails?.subscription?.features?.maxUsers || 1}
          currentUserCount={combinedUsers.length}
        />
      )}

      {showRenewModal && (
        <Modal
          opened={true}
          onClose={() => !renewLoading && setShowRenewModal(false)}
          title="Renew Account"
          size="md"
        >
          <div className="space-y-4">
            <Text>
              Current expiration: <strong>{formattedExpiration}</strong>
            </Text>
            <Text>
              Renewal adds 1 year to the current end date. Cost is ${pricePerSeat}/seat/year, paid once — it does not renew automatically.
            </Text>
            <NumberInput
              label="Number of seats"
              min={1}
              max={maxExplorerSeats}
              value={renewSeats}
              onChange={(value) => setRenewSeats(value || 1)}
            />
            {seatsSwitchedOff > 0 && (
              <Text c="orange">
                {activeSeats} people hold active seats. With {renewSeats} seat{renewSeats === 1 ? '' : 's'}, the {seatsSwitchedOff} most recently added will be deactivated after renewal — you can change who in the user list.
              </Text>
            )}
            <Text>
              Total: ${pricePerSeat * (renewSeats || 0)} / year
            </Text>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="default"
                onClick={() => setShowRenewModal(false)}
                disabled={renewLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleRenewConfirm} loading={renewLoading}>
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showOrgSettingsModal && (
        <OrgSettings
          authToken={user.idToken}
          onClose={() => setShowOrgSettingsModal(false)}
          organizationId={organizationName}
          currentUsers={orgUsers}
          currentOrgLeader={subscriptionDetails?.subscription?.orgLeader}
          onUpdate={updateAllUsers}
        />
      )}
    </div>
  );
};

export default Subscription; 